import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { ParticipantsService } from '@app/core/services/participants.service';
import { Participant } from '@app/shared/models/participant';
import * as _ from 'lodash-es';
import {
  AddressParticipantOptions
} from '@app/modules/case/components/address-participant/address-participant.component';
import { ParticipantSubTypes, ParticipantTypes } from '@app/shared/models/participant-type';
import { CaseService } from '@app/core/services/case.service';
import { InsuranceTypes } from '@app/shared/models/insurance-type';
import { OP, OpData } from '@app/core/services/base/base-service-with-bundle.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ParticipantComponent } from '@app/modules/case/components/participant-base/participant.component';
import { BaseWithIdList } from '@app/shared/models/list';
import { Case } from '@app/shared/models/case';
import { PatchValue } from '@app/shared/components/base/base-patch.component';
import { TranslationEntry } from '@app/shared/models/translatable';
import {AddressBookEntryService} from "@app/core/services/address-book-entry.service";

@Component({
  selector: 'participant-details',
  templateUrl: './participant-details.component.html',
  styleUrls: ['./participant-details.component.scss']
})
export class ParticipantDetailsComponent extends ParticipantComponent implements OnInit {

  @Input()
  public case: Case;

  @Output()
  public caseUpdate = new EventEmitter<PatchValue>();

  @Output()
  public caseMultiUpdate = new EventEmitter<OpData[]>();

  @Input()
  public participants: BaseWithIdList<Participant>;

  get isCompany() {
    return this.participant.is_company ? 'true' : 'false';
  }

  set isCompany(value: string) {
    this.participant.is_company = value === 'true';
  }

  public addressParticipantOptions: AddressParticipantOptions[];
  public extraTypes: TranslationEntry[];
  public disabledTypes: string[];

  private readonly customerExtraTypes = [
    ParticipantTypes.VEHICLE_OWNER,
    ParticipantTypes.VEHICLE_HOLDER,
    ParticipantTypes.DRIVER,
    ParticipantTypes.POLICY_HOLDER
  ];

  private readonly accidentOpponentExtraTypes = [
    ParticipantTypes.ACCIDENT_OPPONENT_VEHICLE_DRIVER,
    ParticipantTypes.ACCIDENT_OPPONENT_POLICY_HOLDER
  ];

  get additionalTypeTitle() {
    return this.updated.isOfType(ParticipantTypes.CUSTOMER.key) ?
        marker('detailed_processing.participant.title.customer_also') : this.updated.isOfType(ParticipantTypes.ACCIDENT_OPPONENT_VEHICLE_OWNER.key) ?
            marker('detailed_processing.participant.title.accident_opponent_also') : '';
  }

  get ParticipantTypes() {
    return ParticipantTypes;
  }

  get ParticipantSubTypes() {
    return ParticipantSubTypes;
  }

  get InsuranceTypes() {
    return InsuranceTypes;
  }

  get showSubTypes(): boolean {
    return this.participant.isOfType(ParticipantTypes.WITNESS.key);
  }

  get showBankInfo(): boolean {
    return this.participant.isOfType(ParticipantTypes.CUSTOMER.key) ||
        this.participant.isOfType(ParticipantTypes.DRIVER.key) ||
        this.participant.isOfType(ParticipantTypes.LAWYER.key) ||
        this.participant.isOfType(ParticipantTypes.POLICY_HOLDER.key) ||
        this.participant.isOfType(ParticipantTypes.VEHICLE_HOLDER.key) ||
        this.participant.isOfType(ParticipantTypes.VEHICLE_OWNER.key);
  }

  get showCustomerInsuranceInfo(): boolean {
    return this.participant.isOfType(ParticipantTypes.VEHICLE_INSURANCE.key);
  }

  get showOpponentInsuranceInfo(): boolean {
    return this.participant.isOfType(ParticipantTypes.ACCIDENT_OPPONENT_INSURANCE.key);
  }

  get showReferenceNumber(): boolean {
    const showFor = [
        ParticipantTypes.ASSESSOR,
        ParticipantTypes.ACCIDENT_OPPONENT_INSURANCE,
        ParticipantTypes.BODY_SHOP,
        ParticipantTypes.LAWYER,
        ParticipantTypes.LEGAL_EXPENSES_INSURANCE,
        ParticipantTypes.RENTAL_CAR_COMPANY,
        ParticipantTypes.SALES_MANAGER,
        ParticipantTypes.SERVICE_CONSULTANT,
        ParticipantTypes.TOWING_COMPANY,
        ParticipantTypes.VEHICLE_INSURANCE
    ];

    return !!showFor.find(type => this.participant.isOfType(type.key));
  }

  get coveredClaimTypeIsFull(): boolean  {
    return this.case.insurance_covered_claim_type_id === InsuranceTypes.FULL_COMPREHENSIVE.key;
  }

  set coveredClaimTypeIsFull(value: boolean) {
    this.updateInsuranceType(InsuranceTypes.FULL_COMPREHENSIVE, value);
  }

  get coveredClaimTypeIsPartial(): boolean {
    return this.case.insurance_covered_claim_type_id === InsuranceTypes.PARTIAL_COMPREHENSIVE.key;
  }

  set coveredClaimTypeIsPartial(value: boolean) {
    this.updateInsuranceType(InsuranceTypes.PARTIAL_COMPREHENSIVE, value);
  }

  get coveredClaimTypeIsLiability(): boolean {
    return this.case.insurance_covered_claim_type_id === InsuranceTypes.LIABILITY.key;
  }

  set coveredClaimTypeIsLiability(value: boolean)  {
    this.updateInsuranceType(InsuranceTypes.LIABILITY, value);
  }

  constructor(
      protected componentServiceBundle: ComponentServiceBundle,
      protected participantsService: ParticipantsService,
      protected caseService: CaseService,
      protected addressBookEntryService: AddressBookEntryService
  ) {
    super(componentServiceBundle, participantsService);

    this.addressParticipantOptions = [
      AddressParticipantOptions.TITLE,
      AddressParticipantOptions.NAME,
      AddressParticipantOptions.MULTI_NAMES,
      AddressParticipantOptions.ADDRESS,
      AddressParticipantOptions.PHONE,
      AddressParticipantOptions.PHONE2,
      AddressParticipantOptions.EMAIL,
      AddressParticipantOptions.FAX,
      AddressParticipantOptions.COUNTRY
    ];
  }

  public ngOnInit() {
    super.ngOnInit();
    this.setParticipant(this.participant);
  }

  private setParticipant(participant: Participant) {
    this.getDisabledTypes(participant);
    this.original = participant;
    this.updated = _.cloneDeep(participant);
  }

  public getDisabledTypes(participant: Participant): void {
    if (participant && participant.isOfType(ParticipantTypes.CUSTOMER.key)) {
      this.getDisabledTypesForCustomer();
    } else if (participant && participant.isOfType(ParticipantTypes.ACCIDENT_OPPONENT_VEHICLE_OWNER.key)) {
      this.getDisabledTypesForAccidentOpponent();
    } else {
      this.extraTypes = null;
      this.disabledTypes = null;
    }
  }

  private getDisabledTypesForCustomer(): void {
    this.extraTypes = this.customerExtraTypes;
    this.disabledTypes = this.fillDisabledTypes(ParticipantTypes.CUSTOMER);
  }

  private getDisabledTypesForAccidentOpponent(): void {
    this.extraTypes = this.accidentOpponentExtraTypes;
    this.disabledTypes = this.fillDisabledTypes(ParticipantTypes.ACCIDENT_OPPONENT_VEHICLE_OWNER);
  }

  private fillDisabledTypes(excludedType: TranslationEntry): string[] {
    const disabled = [];
    _.each(this.participants.items, (part) => {
      if (!part.isOfType(excludedType.key)) {
        _.forEach(part.type, (type) => {
          const isExtraType = this.extraTypes.find(cType => cType.key === type);
          if (isExtraType) {
            disabled.push(type);
          }
        });
      }
    });

    return disabled;
  }

  private updateInsuranceType(type: TranslationEntry, value: boolean): void {
    const translated = this.translateService.instant(type.trans);
    const opData = [];

    opData.push({
      op: OP.replace,
      path: 'insurance_covered_claim_type_id',
      value: value ? type.key : null
    });

    opData.push({
      op: OP.replace,
      path: 'insurance_covered_claim_type',
      value: value ? translated : null
    });

    this.caseMultiUpdate.emit(opData);
  }

  public patchOpDataArray(patchDataArray: OpData[], callbackFn?: Function): void {
    const self = this;
    super.patchOpDataArray(patchDataArray, (callbackFn || function () {
      self.updateParticipantInList();
    }));
  }

  public onChildParticipantUpdate(participant: Participant): void {
    this.setParticipant(participant);
    this.updateParticipantInList();
  }

  private updateParticipantInList(): void {
    const found = this.participants.items.find(part => part.id === this.original.id);
    const idx = this.participants.items.indexOf(found);
    if (idx > -1) {
      this.original.actionListDisplayName = this.participantsService.getTranslatedParticipantTypes(this.original);
      this.participants.items[idx] = this.original;
    }
  }

  public patchAndUpdateCase(casePropName: string, value: any): void {
    const patchValue = {
      propertyName: casePropName,
      value: value
    };

    this.caseUpdate.emit(patchValue);
  }

  public openImportFromAddressBookModal(participant: Participant): void {

    this.addressBookEntryService.openSearchAddressBookEntryModal()
        .subscribe(selectedAddressBookEntry => {

          if( selectedAddressBookEntry ) {

            const patchData: OpData[] = [
              {
                op: OP.replace,
                path: 'source',
                value: 'ADDRESSBOOK'
              },
              {
                op: OP.replace,
                path: 'source_id',
                value: selectedAddressBookEntry.id
              }
            ];

            this.patchOpDataArray(patchData);
          }


        }
    );

  }
}
